import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DOCUMENT, Inject, Input, OnInit} from '@angular/core';
import {Actions} from '@ngrx/effects';
import {RoutingService, TranslationService, WindowRef} from '@spartacus/core';
import {LaunchDialogService} from '@spartacus/storefront';
import {BehaviorSubject, Observable} from 'rxjs';

import {AimoRoutingService} from '../../../cms-structure/routing/aimo-routing.service';
import {AimoDayGroupedOrderHistory} from '../../../model/order.model';
import {AimoFacet} from '../../../model/product.model';
import {AimoUser} from '../../../model/user.model';
import {AimoActiveCartService} from '../../../service/cart/aimo-active-cart.service';
import {AIMO_CART_UPDATE_HEADER_SUCCESS} from '../../../service/cart/aimo-cart.action';
import {GTMCalendarSource} from '../../../service/gtm/aimo-gtm.model';
import {AimoGtmService} from '../../../service/gtm/aimo-gtm.service';
import {AimoUserService} from '../../../service/user/aimo-user.service';
import {AimoClaimsDialogData, CLAIMS} from '../../claims/claims-modal/aimo-claims-layout.config';
import {
    AimoOrderTemplateDialogData,
    ORDER_TEMPLATE,
    ProductWrapper
} from '../../order-template/order-template-modal/aimo-order-template-layout.config';
import {AimoReorderHistoryDialogData, REORDER_HISTORY} from '../reorder-modal/reorder-layout.config';
import {openCloseSpinner} from "../../shared/utils/spinner/aimo-spinner-utils";

@Component({
    selector: 'aimo-order-history-day',
    templateUrl: './aimo-order-history-day.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AimoOrderHistoryDayComponent implements OnInit {

    currentDay: BehaviorSubject<AimoDayGroupedOrderHistory> = new BehaviorSubject<AimoDayGroupedOrderHistory>(undefined);
    @Input() dayInitialized = false;

    @Input()
    set day(d: AimoDayGroupedOrderHistory) {
        this.currentDay.next(d);
        this.initDay(d);
    }

    @Input() open = false;
    @Input() selectedSearchFacets: AimoFacet[] = [];
    @Input() searchTerm: string;

    deliveryNotes: string[];
    isEditable: boolean;
    user$: Observable<AimoUser>;

    constructor(
        protected activeCartService: AimoActiveCartService,
        protected windowRef: WindowRef,
        protected actions$: Actions,
        protected routingService: RoutingService,
        protected launchDialogService: LaunchDialogService,
        protected translation: TranslationService,
        protected aimoRoutingService: AimoRoutingService,
        protected gtmService: AimoGtmService,
        protected userService: AimoUserService,
        protected cdr: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: Document,
    ) {
    }

    ngOnInit(): void {
        this.user$ = this.userService.get();
    }

    private initDay(day: AimoDayGroupedOrderHistory) {
        this.deliveryNotes = Array.from(
            new Set(
                day?.orders?.flatMap((o) =>
                    o.entries.map((e) => e.invoiceDocumentNumber).filter((n) => n !== undefined),
                ),
            ),
        );
        this.isEditable = day?.orders?.flatMap((o) => o.entries.filter((e) => e.editable)).length > 0;
    }

    toggleDay(day: AimoDayGroupedOrderHistory): void {
        this.open = !this.open;
        if (this.open && !this.dayInitialized) {
            openCloseSpinner(this.document, false);
            this.userService.getOrderHistory({
                from: 0,
                size: 1000,

                facets: this.selectedSearchFacets,
                deliveryDate: {
                    from: new Date(day.date),
                    to: new Date(day.date)
                },
                searchTerm: this.searchTerm,
                loadAllDetails: true
            }).subscribe(o => {
                const newDay = {...o.dayGroupedOrders[0]};
                this.currentDay.next(newDay);
                this.initDay(newDay);
                this.dayInitialized = true;
                this.cdr.detectChanges();
                openCloseSpinner(this.document, false);
                this.gtmService.expandOrderHistory(newDay);
            });
            this.gtmService.expandOrderHistory(day);
        }
    }

    print(): void {
        this.document.defaultView.window.print();
    }

    exportOrderToExcel(day: AimoDayGroupedOrderHistory): void {
        this.activeCartService.exportCartToExcel(day.date, undefined, this.selectedSearchFacets);
    }

    edit(day: AimoDayGroupedOrderHistory): void {
        this.activeCartService.updateRequestedDate(day.date, GTMCalendarSource.orderHistory);

        const sub = this.actions$.subscribe((action) => {
            if (action.type === AIMO_CART_UPDATE_HEADER_SUCCESS) {
                this.routingService.go('/cart', {queryParams: {openOrders: day.orderNumbers.join(',')}});
                sub.unsubscribe();
            }
        });
    }

    reOrder(day: AimoDayGroupedOrderHistory): void {
        this.launchDialogService.closeDialog('closed');
        this.launchDialogService.openDialogAndSubscribe(REORDER_HISTORY, undefined, {
            day: day,
        } as AimoReorderHistoryDialogData);
    }

    downloadDeliveryNote(invoiceId: string, name: string): void {
        this.translation
            .translate(name, {id: invoiceId})
            .subscribe((t) => this.activeCartService.downloadOrderDocument(invoiceId, t, true))
            .unsubscribe();
    }

    claims(day: AimoDayGroupedOrderHistory): void {
        this.launchDialogService.closeDialog('closed');
        this.launchDialogService.openDialogAndSubscribe(CLAIMS, undefined, {
            day: day,
            editable: !day.claimCreated,
        } as AimoClaimsDialogData);
    }

    addToOrderTemplate(day: AimoDayGroupedOrderHistory, sourceId: string): void {
        this.launchDialogService.closeDialog(null);
        this.launchDialogService.openDialogAndSubscribe(ORDER_TEMPLATE, undefined, {
            orderHistoryDay: day.date,
            addToTemplateProducts: day.orders
                .flatMap(d => d.entries)
                .map(e => new ProductWrapper(e.product, e.quantity))
                .reduce((acc, curr) => {
                    if (!acc.find(p => p.product.code === curr.product.code)) {
                        acc.push(curr);
                    }
                    return acc;
                }, []),
            createNew: false,
            checkoutPage: true,
            focusIdAfterClose: sourceId,
        } as AimoOrderTemplateDialogData);
    }

    isPikatukku(): boolean {
        return this.aimoRoutingService.isPikatukku();
    }

    pushGtmEvent(event: string): void {
        this.gtmService.pushEvent(event);
    }
}
